import { BugReport } from '@mui/icons-material';
import { Button, Card, CardContent, CardHeader, CircularProgress } from '@mui/material';
import { useState } from 'react';

import { CardTitle } from './card-title';
import APIService from '@services/APIService';
import { useAccessToken } from '@features/user';

interface DebugCardProps {
  transcriptionId?: string;
  noteProcessingId?: string;
  noteId?: string;
  title?: string;
}

export function DebugCard({
  transcriptionId,
  noteProcessingId,
  noteId,
  title = 'Debug with 3rd Party Services',
}: DebugCardProps) {
  const [isGeneratingHoneycomb, setIsGeneratingHoneycomb] = useState(false);
  const { getAccessToken } = useAccessToken();

  const hasValidParams = transcriptionId || noteProcessingId || noteId;

  const handleOpenHoneycomb = async () => {
    if (!hasValidParams) return;

    setIsGeneratingHoneycomb(true);
    try {
      const accessToken = await getAccessToken();
      const params = new URLSearchParams();

      if (transcriptionId) {
        params.append('transcription_id', transcriptionId);
      }
      if (noteProcessingId) {
        params.append('note_processing_id', noteProcessingId);
      }
      if (noteId) {
        params.append('note_id', noteId);
      }

      params.append('time_range_hours', '24');
      params.append('team', 'smartscribe');
      params.append('environment', 'production');

      const response = await APIService.makeAPIGetRequest({
        requestString: `/v2/admin/observability/honeycomb/note-debugging-url?${params}`,
        accessToken,
      });

      if (response.ok) {
        // Open the returned Honeycomb URL in a new tab
        window.open(response.value.url, '_blank');
      } else {
        console.error('Failed to generate Honeycomb URL:', response.error);
        // TODO: Show user-friendly error message
      }
    } catch (error) {
      console.error('Error generating Honeycomb URL:', error);
      // TODO: Show user-friendly error message
    } finally {
      setIsGeneratingHoneycomb(false);
    }
  };

  if (!hasValidParams) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BugReport className="mr-2 h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">No debugging parameters available for this item.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <BugReport className="mr-2 h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Debug this item using 3rd party observability tools.
          </div>

          <div className="space-y-2">
            {transcriptionId && (
              <div className="text-sm">
                <span className="text-gray-500">Transcription ID:</span>
                <div className="text-gray-900 font-mono text-xs mt-1 break-all">
                  {transcriptionId}
                </div>
              </div>
            )}
            {noteProcessingId && (
              <div className="text-sm">
                <span className="text-gray-500">Note Processing ID:</span>
                <div className="text-gray-900 font-mono text-xs mt-1 break-all">
                  {noteProcessingId}
                </div>
              </div>
            )}
            {noteId && (
              <div className="text-sm">
                <span className="text-gray-500">Note ID:</span>
                <div className="text-gray-900 font-mono text-xs mt-1 break-all">{noteId}</div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenHoneycomb}
              disabled={isGeneratingHoneycomb}
              startIcon={isGeneratingHoneycomb ? <CircularProgress size={16} /> : <BugReport />}
              className="w-full"
            >
              {isGeneratingHoneycomb ? 'Generating URL...' : 'View in Honeycomb'}
            </Button>

            {/* Placeholder for future services */}
            <Button variant="outlined" size="small" disabled className="w-full opacity-50">
              Coming soon: Additional debug tools
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
